import React, { useState, useRef, useEffect } from 'react';
import {
	ArrowRight,
	Calendar,
	ChevronLeft,
	ChevronRight,
} from '@velocity-exchange/icons';
import { Updater, useImmer } from 'use-immer';
import ReactDOM from 'react-dom';
import { cj, cm } from 'src/utils/classNames';
import dayjs, { Dayjs } from 'dayjs';
import BottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/BottomDrawer';
import useCloseBottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/hooks/useCloseBottomDrawer';
import { WithClassName, WithRef } from 'src/utils/UtilityTypes';
import { Z_INDEX } from 'src/constants/zIndex';
import Divider from 'src/components/primitives/Divider/Divider';
import Button from 'src/components/primitives/Button/Button';
import { getColorVar } from 'src/utils/theme/colorVars';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import { getRadioItemCardClassName } from 'src/components/primitives/RadioGroup/utils';
import { STANDARD_DATE_FORMAT } from 'src/constants/dates';

/**
 * Immutable class to manage the buffered selection state for date picking.
 * Treats selected dates like a LIFO queue with max length 2.
 */
class BufferedSelectionState {
	private readonly dates: (Dayjs | null)[];

	constructor(dates: (Dayjs | null)[] = []) {
		// Ensure we never have more than 2 dates
		this.dates = dates.slice(0, 2);
	}

	/**
	 * Returns a new BufferedSelectionState with the date added
	 * If we already have 2 dates, removes the oldest one
	 */
	addDate(date: Dayjs): BufferedSelectionState {
		const newDates = [date, ...this.dates.slice(0, 1)];
		return new BufferedSelectionState(newDates);
	}

	/**
	 * Returns the most recently added date, or null if no dates
	 */
	getPreviousDate(): Dayjs | null {
		return this.dates[0] || null;
	}

	/**
	 * Returns the chronologically earlier date, or null if less than 2 dates
	 */
	getEarlierDate(): Dayjs | null {
		if (this.dates.length < 2 || !this.dates[0] || !this.dates[1]) {
			return null;
		}
		return this.dates[0].isBefore(this.dates[1])
			? this.dates[0]
			: this.dates[1];
	}

	/**
	 * Returns the chronologically later date, or null if less than 2 dates
	 */
	getLaterDate(): Dayjs | null {
		if (this.dates.length < 2 || !this.dates[0] || !this.dates[1]) {
			return null;
		}
		return this.dates[0].isAfter(this.dates[1]) ? this.dates[0] : this.dates[1];
	}

	/**
	 * Returns true if we have 2 valid dates selected
	 */
	hasCompleteSelection(): boolean {
		return this.dates.length === 2 && this.dates.every((date) => date !== null);
	}

	/**
	 * Returns a new empty BufferedSelectionState
	 */
	clear(): BufferedSelectionState {
		return new BufferedSelectionState();
	}
}

interface DateRangeDisplayProps {
	dateRange: DateRange;
	onClick?: () => void;
}

export type DateRange = {
	isDefault: boolean;
	startDate: Dayjs;
	endDate: Dayjs;
};

const formatInlineDate = (date: Dayjs): string => {
	if (!date) {
		return '';
	}

	return date.format(STANDARD_DATE_FORMAT);
};

const DatePickerTrigger = ({
	className,
	dateRange,
	onClick,
	ref,
}: WithClassName<WithRef<DateRangeDisplayProps, HTMLButtonElement>>) => {
	return (
		<button
			className={cj(
				'flex items-center gap-x-xs rounded-sm border px-md py-sm select-none',
				'cursor-pointer border-input-border bg-input-bg typo-label-sm text-text-default hover:bg-button-disabled',
				className
			)}
			onClick={onClick}
			ref={ref}
		>
			{formatInlineDate(dateRange.startDate)}
			<ChevronRight size={16} />
			{formatInlineDate(dateRange.endDate)}
			<Calendar size={16} />
		</button>
	);
};

const isSameDate = (date1: Dayjs, date2: Dayjs | null) => {
	if (!date2) {
		return false;
	}
	return date1.isSame(date2, 'day');
};

const getValidationMessage = (
	failure: ValidationFailure,
	validationConfig: ValidationConfig | undefined
): string => {
	if (!validationConfig) {
		return '';
	}

	switch (failure) {
		case 'MIN_DATE':
			if (!validationConfig.minDate) {
				return 'Start date is too early.';
			}
			return `Start date cannot be before ${formatInlineDate(
				validationConfig.minDate
			)}`;
		case 'MAX_DATE':
			if (!validationConfig.maxDate) {
				return 'End date is too late.';
			}
			return `End date cannot be after ${formatInlineDate(
				validationConfig.maxDate
			)}`;
		case 'MAX_DAYS':
			return `Date range cannot exceed ${validationConfig.maxDays} days`;
		default:
			return '';
	}
};

const validateStartDate = (
	start: Dayjs,
	validationConfig: ValidationConfig
): ValidationFailure => {
	if (
		validationConfig?.minDate &&
		start.isBefore(validationConfig.minDate, 'day')
	) {
		return 'MIN_DATE';
	}
	return null;
};

const validateEndDate = (
	end: Dayjs,
	validationConfig: ValidationConfig
): ValidationFailure => {
	if (
		validationConfig?.maxDate &&
		end.isAfter(validationConfig.maxDate, 'day')
	) {
		return 'MAX_DATE';
	}
	return null;
};

const validateDateRange = (
	start: Dayjs,
	end: Dayjs,
	validationConfig: ValidationConfig | undefined
): ValidationFailure => {
	if (!validationConfig) {
		return null;
	}

	const startFailure = validateStartDate(start, validationConfig);
	if (startFailure) {
		return startFailure;
	}

	const endFailure = validateEndDate(end, validationConfig);
	if (endFailure) {
		return endFailure;
	}

	if (validationConfig?.maxDays) {
		const daysDiff = end.diff(start, 'day'); // +1 to include the end date
		if (daysDiff > validationConfig.maxDays) {
			return 'MAX_DAYS';
		}
	}
	return null;
};

const validateBufferedStart = (
	start: Dayjs,
	validationConfig: ValidationConfig | undefined
): ValidationFailure => {
	if (!validationConfig) {
		return null;
	}

	const startFailure = validateStartDate(start, validationConfig);
	if (startFailure) {
		return startFailure;
	}
	return null;
};

export type ValidationConfig = {
	maxDays?: number;
	minDate?: Dayjs;
	maxDate?: Dayjs;
	allowFutureDates: boolean;
};

type DatePickerProps = {
	dateRange: DateRange;
	setDateRange: Updater<Pick<DateRange, 'startDate' | 'endDate'>>;
	validationConfig?: ValidationConfig;
	customMessage?: string;
};

interface DatePickerInteractiveDisplayProps {
	dateRange: DateRange;
	onDateChange: (start: Dayjs, end: Dayjs) => void;
	onToggleDisplay: () => void;
	triggerRef: React.RefObject<HTMLButtonElement | null>;
	validationConfig?: ValidationConfig;
	customMessage?: string;
}

interface MobileDatePickerDisplayProps {
	dateRange: DateRange;
	onDateChange: (start: Dayjs, end: Dayjs) => void;
	onClose: () => void;
	validationConfig?: ValidationConfig;
	customMessage?: string;
}

type ValidationFailure = 'MIN_DATE' | 'MAX_DATE' | 'MAX_DAYS' | null;

const getSelectionMessage = (
	validationError: string,
	validationConfig: ValidationConfig | undefined
) => {
	if (!validationConfig) {
		return {
			shouldShowMessage: false,
			messageToShow: '',
			messageSemantics: 'neutral',
		};
	}
	const shouldShowMessage = validationError || validationConfig?.maxDays;

	const messageToShow = validationError
		? validationError
		: `You can select a maximum of ${validationConfig?.maxDays} days`;

	const messageSemantics = validationError ? 'error' : 'neutral';

	return {
		shouldShowMessage,
		messageToShow,
		messageSemantics,
	};
};

const DateSelectionMessageDisplay = ({
	validationError,
	validationConfig,
	customMessage,
}: {
	validationError?: string | null;
	validationConfig?: ValidationConfig;
	customMessage?: string;
}) => {
	const { shouldShowMessage, messageToShow, messageSemantics } =
		getSelectionMessage(validationError ?? '', validationConfig);

	if (!shouldShowMessage && !customMessage) {
		return null;
	}

	const content = (() => {
		if (customMessage) {
			return customMessage;
		}

		if (shouldShowMessage) {
			return messageToShow;
		}

		return null;
	})();

	return (
		<div
			className={cj(
				'flex flex-col gap-y-sm typo-body-2xs text-text-secondary',
				!customMessage && messageSemantics === 'error' && 'text-star-yellow'
			)}
		>
			{content}
		</div>
	);
};

const CalendarDateCell = ({
	selectedStartDate,
	selectedEndDate,
	cellDate,
	bufferedSelection,
	handleDateClick,
	handleDateHover,
	hoveredDate,
	isOutOfMonth,
	validationConfig,
}: {
	selectedStartDate: Dayjs;
	selectedEndDate: Dayjs;
	cellDate: Dayjs;
	bufferedSelection: BufferedSelectionState;
	handleDateClick: (day: Dayjs) => void;
	handleDateHover?: (day: Dayjs) => void;
	hoveredDate?: Dayjs | null;
	isOutOfMonth?: boolean;
	validationConfig?: ValidationConfig;
}) => {
	let effectiveStart: Dayjs | null = selectedStartDate;
	let effectiveEnd: Dayjs | null = selectedEndDate;

	const isBeforeMinDate =
		validationConfig?.minDate &&
		cellDate.isBefore(validationConfig.minDate, 'day');

	const isBeforeMaxDays =
		validationConfig?.maxDays &&
		bufferedSelection &&
		!bufferedSelection.hasCompleteSelection() &&
		bufferedSelection.getPreviousDate() &&
		cellDate.isBefore(
			bufferedSelection
				.getPreviousDate()
				?.subtract(validationConfig.maxDays, 'day')
		);

	const isAfterMaxDate =
		validationConfig?.maxDate &&
		cellDate.isAfter(validationConfig.maxDate, 'day');

	const isAfterMaxDays =
		validationConfig?.maxDays &&
		bufferedSelection &&
		!bufferedSelection.hasCompleteSelection() &&
		bufferedSelection.getPreviousDate() &&
		cellDate.isAfter(
			bufferedSelection.getPreviousDate()?.add(validationConfig.maxDays, 'day')
		);

	// Check if the date should be disabled
	const isTooEarly = isBeforeMinDate || isBeforeMaxDays;
	const isTooLate = isAfterMaxDate || isAfterMaxDays;
	const isFutureDate = cellDate.isAfter(dayjs(), 'day');
	const isDisabled =
		isTooEarly ||
		isTooLate ||
		(!validationConfig?.allowFutureDates && isFutureDate);

	const showHoverEffects =
		!bufferedSelection.hasCompleteSelection() &&
		bufferedSelection.getPreviousDate() &&
		hoveredDate;

	// Only show hover effects if we have an incomplete selection
	if (showHoverEffects) {
		const bufferedDate = bufferedSelection.getPreviousDate();

		if (bufferedDate) {
			effectiveStart = hoveredDate.isBefore(bufferedDate)
				? hoveredDate
				: bufferedDate;

			effectiveEnd = hoveredDate.isAfter(bufferedDate)
				? hoveredDate
				: bufferedDate;
		}
	} else if (bufferedSelection.hasCompleteSelection()) {
		effectiveStart = bufferedSelection.getEarlierDate();
		effectiveEnd = bufferedSelection.getLaterDate();
	} else if (bufferedSelection.getPreviousDate()) {
		effectiveStart = bufferedSelection.getPreviousDate();
		effectiveEnd = null;
	}

	const isStart = isSameDate(cellDate, effectiveStart);
	const isEnd = isSameDate(cellDate, effectiveEnd);

	const isInRange =
		cellDate.isSame(effectiveStart, 'day') ||
		cellDate.isSame(effectiveEnd, 'day') ||
		(cellDate.isAfter(effectiveStart) && cellDate.isBefore(effectiveEnd));

	return (
		<div
			className={cm(
				'flex h-8 items-center justify-center overflow-hidden p-xs',
				'typo-body-sm text-text-default select-none',
				!isDisabled && 'cursor-pointer',
				isInRange && 'bg-purple-bg',
				isStart && 'rounded-l-sm bg-button-selector',
				isEnd && 'rounded-r-sm bg-button-selector',
				(isOutOfMonth || isDisabled) && 'text-text-tertiary'
			)}
			onClick={() => !isDisabled && handleDateClick(cellDate)}
			onMouseEnter={
				// Only enable hover handler if selection is incomplete and date is not disabled
				!isDisabled &&
				!bufferedSelection.hasCompleteSelection() &&
				handleDateHover
					? () => handleDateHover(cellDate)
					: undefined
			}
		>
			{cellDate.format('DD')}
		</div>
	);
};

const CalendarHeaderCells = () => {
	return (
		<>
			{['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
				<div
					key={day}
					className="text-center typo-label-xs text-text-secondary"
				>
					{day}
				</div>
			))}
		</>
	);
};

const CalendarGrid = ({
	days,
	bufferedSelection,
	selectedDateRange,
	handleDateClick,
	handleDateHover,
	handleMouseLeave,
	hoveredDate,
	validationConfig,
}: {
	days: { date: Dayjs; isOutOfMonth: boolean }[];
	bufferedSelection: BufferedSelectionState;
	selectedDateRange: DateRange;
	handleDateClick: (day: Dayjs) => void;
	handleDateHover?: (day: Dayjs) => void;
	handleMouseLeave: () => void;
	hoveredDate?: Dayjs | null;
	validationConfig?: ValidationConfig;
}) => {
	return (
		<div className="grid grid-cols-7">
			<CalendarHeaderCells />
			<div
				className="col-span-7 grid grid-cols-7"
				onMouseLeave={handleMouseLeave}
			>
				{days.map((day, index) => (
					<CalendarDateCell
						key={index}
						selectedStartDate={selectedDateRange.startDate}
						selectedEndDate={selectedDateRange.endDate}
						cellDate={day.date}
						bufferedSelection={bufferedSelection}
						handleDateClick={handleDateClick}
						handleDateHover={handleDateHover}
						hoveredDate={hoveredDate}
						isOutOfMonth={day.isOutOfMonth}
						validationConfig={validationConfig}
					/>
				))}
			</div>
		</div>
	);
};

const useDatePickerState = ({
	dateRange,
	onDateChange,
	onToggleDisplay,
	validationConfig,
	triggerRef,
}: DatePickerInteractiveDisplayProps) => {
	const [bufferedSelection, setBufferedSelection] =
		useState<BufferedSelectionState>(new BufferedSelectionState());
	const [hoveredDate, setHoveredDate] = useState<Dayjs | null>(null);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [currentMonth, setCurrentMonth] = useState(
		dateRange?.startDate
			? dayjs(dateRange.startDate).startOf('day')
			: dayjs().startOf('day')
	);
	const nextMonth = currentMonth.add(1, 'month');
	const [position, setPosition] = useState<{
		top: number;
		left: number;
	} | null>(null);
	const portalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const updatePosition = () => {
			if (triggerRef.current) {
				const rect = triggerRef.current.getBoundingClientRect();
				setPosition({
					top: rect.bottom + window.scrollY,
					left: rect.left + window.scrollX,
				});
			}
		};

		updatePosition();
		window.addEventListener('scroll', updatePosition);
		window.addEventListener('resize', updatePosition);

		return () => {
			window.removeEventListener('scroll', updatePosition);
			window.removeEventListener('resize', updatePosition);
		};
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				portalRef.current &&
				!portalRef.current.contains(event.target as Node)
			) {
				onToggleDisplay();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [onToggleDisplay]);

	const handleConfirm = () => {
		if (!bufferedSelection.hasCompleteSelection()) {
			onToggleDisplay();
			return;
		}

		const startDate = bufferedSelection.getEarlierDate();
		const endDate = bufferedSelection.getLaterDate();

		if (!startDate || !endDate) {
			onToggleDisplay();
			return;
		}

		const failure = validateDateRange(startDate, endDate, validationConfig);

		if (failure) {
			setValidationError(getValidationMessage(failure, validationConfig));
			return;
		}

		onDateChange(startDate, endDate);
		onToggleDisplay();
	};

	const handleDateClick = (date: Dayjs) => {
		const dayDate = date.startOf('day');

		// If clicking a new date, validate it first
		const startFailure = validateBufferedStart(dayDate, validationConfig);
		if (startFailure) {
			setValidationError(getValidationMessage(startFailure, validationConfig));
			return;
		}

		let newBufferedSelection: BufferedSelectionState;

		// If we already have a complete buffered selection, clear it and treat this as a new selection
		if (bufferedSelection.hasCompleteSelection()) {
			newBufferedSelection = bufferedSelection.clear().addDate(dayDate);
		} else {
			// Add the new date to our buffered selection
			newBufferedSelection = bufferedSelection.addDate(dayDate);
		}

		// Check if the new buffered selection is valid
		if (newBufferedSelection.hasCompleteSelection()) {
			const failure = validateDateRange(
				newBufferedSelection.getEarlierDate() ?? dayjs(),
				newBufferedSelection.getLaterDate() ?? dayjs(),
				validationConfig
			);
			if (failure) {
				setValidationError(getValidationMessage(failure, validationConfig));
				return;
			}
		}

		setBufferedSelection(newBufferedSelection);
		setValidationError(null);
	};

	const handleDateHover = (date: Dayjs) => {
		const dayDate = date.startOf('day');
		setHoveredDate(dayDate);
	};

	const handleMouseLeave = () => {
		setHoveredDate(null);
	};

	const handlePrevMonth = () => {
		setCurrentMonth((prev) => prev.subtract(1, 'month'));
	};

	const handleNextMonth = () => {
		setCurrentMonth((prev) => prev.add(1, 'month'));
	};

	const generateCalendarDays = (
		date: Dayjs
	): { date: Dayjs; isOutOfMonth: boolean }[] => {
		const firstDay = date.startOf('month');
		const daysInMonth = date.daysInMonth();
		const startingDay = firstDay.day();

		const days: { date: Dayjs; isOutOfMonth: boolean }[] = [];

		// Add days from previous month
		const prevMonth = date.subtract(1, 'month');
		const daysInPrevMonth = prevMonth.daysInMonth();
		for (let i = 0; i < startingDay; i++) {
			const prevMonthDay = prevMonth.date(
				daysInPrevMonth - startingDay + i + 1
			);
			days.push({ date: prevMonthDay, isOutOfMonth: true });
		}

		// Add days of current month
		for (let i = 1; i <= daysInMonth; i++) {
			days.push({ date: date.date(i), isOutOfMonth: false });
		}

		// Add days from next month
		const remainingCells = 42 - days.length;
		const nextMonth = date.add(1, 'month');
		for (let i = 1; i <= remainingCells; i++) {
			days.push({ date: nextMonth.date(i), isOutOfMonth: true });
		}

		return days;
	};

	const confirmButtonEnabled =
		bufferedSelection.hasCompleteSelection() && !validationError;

	// Helper function to determine the effective date range
	const getEffectiveDateRange = () => {
		if (bufferedSelection.hasCompleteSelection()) {
			return {
				start: bufferedSelection.getEarlierDate(),
				end: bufferedSelection.getLaterDate(),
			};
		}

		const bufferedDate = bufferedSelection.getPreviousDate();
		// Only show hover effects if we have an incomplete selection
		if (
			!bufferedSelection.hasCompleteSelection() &&
			bufferedDate &&
			hoveredDate
		) {
			return {
				start: hoveredDate.isBefore(bufferedDate) ? hoveredDate : bufferedDate,
				end: hoveredDate.isAfter(bufferedDate) ? hoveredDate : bufferedDate,
			};
		}

		return {
			start: dateRange.startDate,
			end: dateRange.endDate,
		};
	};

	return {
		bufferedSelection,
		hoveredDate,
		validationError,
		currentMonth,
		nextMonth,
		position,
		portalRef,
		handleConfirm,
		handleDateClick,
		handleDateHover,
		handlePrevMonth,
		handleNextMonth,
		generateCalendarDays,
		handleMouseLeave,
		confirmButtonEnabled,
		getEffectiveDateRange,
	};
};

const DesktopDatePickerInteractiveDisplay: React.FC<
	DatePickerInteractiveDisplayProps
> = ({
	dateRange,
	onDateChange,
	onToggleDisplay,
	triggerRef,
	validationConfig,
	customMessage,
}) => {
	const {
		bufferedSelection,
		hoveredDate,
		validationError,
		currentMonth,
		nextMonth,
		position,
		portalRef,
		handleConfirm,
		handleDateClick,
		handleDateHover,
		handlePrevMonth,
		handleNextMonth,
		generateCalendarDays,
		handleMouseLeave,
		confirmButtonEnabled,
		getEffectiveDateRange,
	} = useDatePickerState({
		dateRange,
		onDateChange,
		onToggleDisplay,
		validationConfig,
		triggerRef,
	});

	const renderCalendarMonth = (date: Dayjs, side: 'left' | 'right') => {
		const days = generateCalendarDays(date);
		const monthYear = date.format('MMM YYYY');

		return (
			<div className="flex w-75 flex-col gap-y-lg">
				<div className="flex items-center justify-between">
					{side === 'left' ? (
						<IconButton
							size="lg"
							variant="tertiary"
							icon={ChevronLeft}
							onClick={handlePrevMonth}
						/>
					) : (
						<div className="size-2xl" />
					)}
					<span className="typo-label-sm text-text-default">{monthYear}</span>
					{side === 'right' ? (
						<IconButton
							size="lg"
							variant="tertiary"
							icon={ChevronRight}
							onClick={handleNextMonth}
						/>
					) : (
						<div className="size-2xl" />
					)}
				</div>
				<CalendarGrid
					days={days}
					bufferedSelection={bufferedSelection}
					selectedDateRange={dateRange}
					handleDateClick={handleDateClick}
					handleDateHover={handleDateHover}
					hoveredDate={hoveredDate}
					handleMouseLeave={handleMouseLeave}
					validationConfig={validationConfig}
				/>
			</div>
		);
	};

	const effectiveDateRange = getEffectiveDateRange();

	return (
		position &&
		ReactDOM.createPortal(
			<div
				className={cj(
					'fixed rounded-sm border border-input-border bg-container-bg p-lg',
					'flex flex-col gap-y-lg',
					Z_INDEX.dropdown
				)}
				style={{
					top: position.top + 8, // 8px gap between popover and trigger.
					left: position.left,
				}}
				ref={portalRef}
			>
				<div className="flex gap-x-8">
					{renderCalendarMonth(currentMonth, 'left')}
					{renderCalendarMonth(nextMonth, 'right')}
				</div>

				<DateSelectionMessageDisplay
					validationError={validationError}
					validationConfig={validationConfig}
					customMessage={customMessage}
				/>

				<Divider />

				<div className="flex justify-between">
					{/* Inline intermediate date display */}
					<div className="flex items-center gap-x-xs typo-label-sm text-text-default select-none">
						{formatInlineDate(effectiveDateRange.start ?? dayjs())}
						<ArrowRight color={getColorVar('text-secondary')} size={16} />
						{formatInlineDate(effectiveDateRange.end ?? dayjs())}
					</div>

					{/* Confirm Button */}
					<Button
						size="md"
						variant="secondary"
						onClick={handleConfirm}
						disabled={!confirmButtonEnabled}
					>
						Confirm
					</Button>
				</div>
			</div>,
			document.body
		)
	);
};

const MobileDatePickerInteractiveDisplay: React.FC<
	MobileDatePickerDisplayProps
> = ({ dateRange, onDateChange, onClose, validationConfig, customMessage }) => {
	const {
		bufferedSelection,
		validationError,
		currentMonth,
		handleConfirm,
		handleDateClick,
		handlePrevMonth,
		handleNextMonth,
		generateCalendarDays,
		handleMouseLeave,
		confirmButtonEnabled,
	} = useDatePickerState({
		dateRange,
		onDateChange,
		onToggleDisplay: onClose,
		validationConfig,
		triggerRef: { current: null }, // Mobile version doesn't need positioning
	});

	const { closeDrawer, isDrawerClosing } = useCloseBottomDrawer({ onClose });

	const days = generateCalendarDays(currentMonth);
	const monthYear = currentMonth.format('MMM YYYY');

	/**
	 * Get the current display dates for the start and end of the date range. If we're in the middle of a buffered selection we should not be displaying the currently set (input props) dates.
	 * @param type
	 * @returns
	 */
	const getDisplayDate = (type: 'start' | 'end'): Dayjs | null => {
		if (bufferedSelection.hasCompleteSelection()) {
			return type === 'start'
				? bufferedSelection.getEarlierDate()
				: bufferedSelection.getLaterDate();
		} else if (bufferedSelection.getPreviousDate()) {
			return bufferedSelection.getPreviousDate();
		}
		return type === 'start' ? dateRange.startDate : dateRange.endDate;
	};

	return (
		<BottomDrawer.Container
			closeDrawer={closeDrawer}
			isDrawerClosing={isDrawerClosing}
		>
			<BottomDrawer.Title closeDrawer={closeDrawer}>
				Select a time range
			</BottomDrawer.Title>
			{/* Date Range Selection Header */}
			<BottomDrawer.Body>
				<div className="flex items-center gap-x-sm">
					<div
						className={getRadioItemCardClassName({
							size: 'md',
							className: 'flex-1 text-text-secondary',
							disabled: true,
						})}
					>
						{formatInlineDate(getDisplayDate('start') ?? dayjs()) || '\u200b'}
					</div>
					<ArrowRight color={getColorVar('text-secondary')} size={20} />
					<div
						className={getRadioItemCardClassName({
							size: 'md',
							className: 'flex-1 text-text-secondary',
							disabled: true,
						})}
					>
						{formatInlineDate(getDisplayDate('end') ?? dayjs()) || '\u200b'}
					</div>
				</div>

				{/* Month Navigation */}
				<div className="flex items-center justify-between">
					<IconButton
						size="lg"
						variant="tertiary"
						icon={ChevronLeft}
						onClick={handlePrevMonth}
					/>
					<span className="typo-label-sm text-text-default">{monthYear}</span>
					<IconButton
						size="lg"
						variant="tertiary"
						icon={ChevronRight}
						onClick={handleNextMonth}
					/>
				</div>

				<CalendarGrid
					days={days}
					bufferedSelection={bufferedSelection}
					selectedDateRange={dateRange}
					handleDateClick={handleDateClick}
					handleMouseLeave={handleMouseLeave}
					validationConfig={validationConfig}
				/>

				<DateSelectionMessageDisplay
					validationError={validationError}
					validationConfig={validationConfig}
					customMessage={customMessage}
				/>
			</BottomDrawer.Body>
			<BottomDrawer.Footer>
				<Button
					size="lg"
					className="w-full"
					disabled={!confirmButtonEnabled}
					onClick={handleConfirm}
				>
					Confirm
				</Button>
			</BottomDrawer.Footer>
		</BottomDrawer.Container>
	);
};

export const DatePicker = ({
	dateRange,
	setDateRange,
	validationConfig = { allowFutureDates: false },
	customMessage,
	className,
}: WithClassName<DatePickerProps>) => {
	const [showInteractiveDisplay, setShowInteractiveDisplay] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const [isClosing, setIsClosing] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleDateChange = (start: Dayjs, end: Dayjs) => {
		setDateRange({
			startDate: start.startOf('day'),
			endDate: end.startOf('day'),
		});
	};

	const onToggleDisplay = () => {
		if (!showInteractiveDisplay && !isClosing) {
			setShowInteractiveDisplay(true);
		}
	};

	const onClose = () => {
		setIsClosing(true);
		setShowInteractiveDisplay(false);
		// Reset isClosing after a short delay, otherwise the interactive display will be shown again
		setTimeout(() => {
			setIsClosing(false);
		}, 100);
	};

	return (
		<>
			<DatePickerTrigger
				className={className}
				dateRange={dateRange}
				onClick={
					!showInteractiveDisplay && !isClosing ? onToggleDisplay : undefined
				}
				ref={triggerRef}
			/>
			{showInteractiveDisplay &&
				(isMobile ? (
					<MobileDatePickerInteractiveDisplay
						dateRange={dateRange}
						onDateChange={handleDateChange}
						onClose={onClose}
						validationConfig={{
							maxDays: validationConfig?.maxDays,
							minDate: validationConfig?.minDate
								? validationConfig.minDate.startOf('day')
								: undefined,
							maxDate: validationConfig?.maxDate
								? validationConfig.maxDate.startOf('day')
								: undefined,
							allowFutureDates: validationConfig?.allowFutureDates,
						}}
						customMessage={customMessage}
					/>
				) : (
					<DesktopDatePickerInteractiveDisplay
						dateRange={dateRange}
						onDateChange={handleDateChange}
						onToggleDisplay={onClose}
						triggerRef={triggerRef}
						validationConfig={{
							maxDays: validationConfig?.maxDays,
							minDate: validationConfig?.minDate
								? validationConfig.minDate.startOf('day')
								: undefined,
							maxDate: validationConfig?.maxDate
								? validationConfig.maxDate.startOf('day')
								: undefined,
							allowFutureDates: validationConfig?.allowFutureDates,
						}}
						customMessage={customMessage}
					/>
				))}
		</>
	);
};

export const useDatePickerStates = (
	defaultDateRange?: Pick<DateRange, 'startDate' | 'endDate'>
) => {
	const [dateRange, _setDateRange] = useImmer<DateRange>({
		isDefault: true,
		startDate: defaultDateRange?.startDate
			? defaultDateRange.startDate.startOf('day')
			: dayjs().startOf('day'),
		endDate: defaultDateRange?.endDate
			? defaultDateRange.endDate.startOf('day')
			: dayjs().startOf('day'),
	});

	const setDateRange = (
		dateRange: Pick<DateRange, 'startDate' | 'endDate'>
	) => {
		_setDateRange((draft) => {
			draft.startDate = dateRange.startDate.startOf('day');
			draft.endDate = dateRange.endDate.startOf('day');
			draft.isDefault = false;
		});
	};

	return { dateRange, setDateRange };
};
